/**
 * Roles and permissions.
 *
 * The demo let one browser tab "act as" any persona. Here a membership binds
 * a user to one party inside one tenant with one role, and every permission
 * is checked server-side. Permissions are `resource:action` strings so the
 * guard can pattern-match without knowing the domain.
 */

export const ROLES = [
  'PLATFORM_ADMIN',
  'OPS',
  'UNDERWRITER',
  'SME',
  'PAYER',
  'LENDER',
  'AUDITOR',
] as const;
export type Role = (typeof ROLES)[number];

export interface RoleMeta {
  role: Role;
  name: string;
  description: string;
  /** Party kind this role must be attached to; null = tenant-level role */
  partyKind: 'SME' | 'PAYER' | 'LENDER' | null;
}

export const ROLE_META: Record<Role, RoleMeta> = {
  PLATFORM_ADMIN: {
    role: 'PLATFORM_ADMIN',
    name: 'Administrator',
    description: 'Programmes, access and integrations',
    partyKind: null,
  },
  OPS: {
    role: 'OPS',
    name: 'Operations',
    description: 'Validate, route, control tower',
    partyKind: null,
  },
  UNDERWRITER: {
    role: 'UNDERWRITER',
    name: 'Underwriter',
    description: 'Manual review of referred deals and limit setting',
    partyKind: null,
  },
  SME: {
    role: 'SME',
    name: 'SME / Supplier',
    description: 'Originate and finance receivables',
    partyKind: 'SME',
  },
  PAYER: {
    role: 'PAYER',
    name: 'Payer / Buyer',
    description: 'Confirm and settle invoices',
    partyKind: 'PAYER',
  },
  LENDER: {
    role: 'LENDER',
    name: 'Lender / Financier',
    description: 'Underwrite and fund advances',
    partyKind: 'LENDER',
  },
  AUDITOR: {
    role: 'AUDITOR',
    name: 'Auditor',
    description: 'Read-only access to deals, ledger and audit trail',
    partyKind: null,
  },
};

export const PERMISSIONS = [
  // deals
  'deal:read',
  'deal:create',
  'deal:submit',
  'deal:validate',
  'deal:review',
  'deal:confirm',
  'deal:dispute',
  'deal:route',
  'deal:offer',
  'deal:disburse',
  'deal:settle',
  'deal:writeoff',
  'deal:cancel',
  // ledger and money
  'ledger:read',
  'ledger:post',
  'statement:read',
  // credit intelligence
  'signal:read',
  'signal:ingest',
  'signal:classify',
  'signal:score',
  'graph:read',
  'limit:read',
  'limit:write',
  // administration
  'party:read',
  'party:write',
  'programme:read',
  'programme:write',
  'user:read',
  'user:write',
  'integration:read',
  'integration:write',
  'audit:read',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  PLATFORM_ADMIN: [...PERMISSIONS],

  OPS: [
    // Ops key in deals that arrive by phone, email or spreadsheet on a
    // supplier's behalf, and must be able to submit them too — otherwise an
    // Ops-originated deal sits in DRAFT with nobody able to move it on.
    'deal:create', 'deal:submit',
    'deal:read', 'deal:validate', 'deal:review', 'deal:route', 'deal:disburse',
    'deal:settle', 'deal:writeoff', 'deal:cancel',
    'ledger:read', 'ledger:post', 'statement:read',
    'signal:read', 'signal:ingest', 'signal:classify', 'signal:score',
    'graph:read', 'limit:read',
    'party:read', 'programme:read', 'user:read', 'integration:read', 'audit:read',
  ],

  UNDERWRITER: [
    'deal:read', 'deal:review', 'deal:validate',
    'ledger:read', 'statement:read',
    'signal:read', 'signal:score', 'graph:read', 'limit:read', 'limit:write',
    'party:read', 'programme:read', 'audit:read',
  ],

  SME: [
    'deal:read', 'deal:create', 'deal:submit', 'deal:offer', 'deal:cancel',
    'ledger:read', 'statement:read',
    'signal:read', 'signal:ingest',
    'party:read', 'programme:read',
  ],

  PAYER: [
    'deal:read', 'deal:confirm', 'deal:dispute',
    'statement:read',
    'party:read',
  ],

  LENDER: [
    'deal:read', 'deal:route', 'deal:disburse',
    'ledger:read', 'statement:read',
    'signal:read', 'graph:read', 'limit:read',
    'party:read', 'programme:read',
  ],

  AUDITOR: [
    'deal:read', 'ledger:read', 'statement:read',
    'signal:read', 'graph:read', 'limit:read',
    'party:read', 'programme:read', 'user:read', 'audit:read',
  ],
};

export function permissionsFor(roles: Role[]): Set<Permission> {
  const out = new Set<Permission>();
  for (const r of roles) for (const p of ROLE_PERMISSIONS[r] ?? []) out.add(p);
  return out;
}

export function hasPermission(roles: Role[], required: Permission): boolean {
  return permissionsFor(roles).has(required);
}
